import { useEffect, useRef, useState } from "react";
import { Sparkles, Send, Loader2, CheckCircle2 } from "lucide-react";
import { API } from "./shared";

const SUGGESTIONS = [
  "Summarise the leads pipeline right now",
  "Which tickets are still open?",
  "List the schools in the pool",
  "Move the newest lead to contacted and assign it to Andrea Asare",
];

export default function Agent({ token, onAuthError }) {
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "I'm your ops agent. I can read live data and take action — summarise the pipeline, add schools to the pool, create tickets, move leads through stages. What should we work on?",
      actions: [],
    },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const patchLast = (fn) =>
    setMessages((m) => {
      const c = [...m];
      c[c.length - 1] = fn({ ...c[c.length - 1] });
      return c;
    });

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "", actions: [] }]);
    try {
      const resp = await fetch(`${API}/agent/chat`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ message: msg }),
      });
      if (resp.status === 401 || resp.status === 403) {
        onAuthError();
        return;
      }
      if (!resp.ok || !resp.body) throw new Error(`HTTP ${resp.status}`);
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          let payload;
          try {
            payload = JSON.parse(line.slice(5));
          } catch {
            continue;
          }
          if (payload.type === "delta") {
            patchLast((last) => ({ ...last, content: last.content + payload.content }));
          } else if (payload.type === "done") {
            patchLast((last) => ({ ...last, actions: payload.actions || [] }));
          } else if (payload.type === "error") {
            patchLast((last) => ({ ...last, content: last.content || payload.message }));
          }
        }
      }
    } catch (e) {
      patchLast(() => ({ role: "assistant", content: "Sorry — I hit an error reaching the agent. Please try again.", actions: [] }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="agent-tab" className="flex h-[calc(100vh-170px)] min-h-[480px] flex-col border border-[#E6E2DC] bg-white">
      <div className="flex items-center gap-3 border-b border-[#E6E2DC] px-5 py-4">
        <span className="flex h-8 w-8 items-center justify-center bg-[#581820] text-[#FAF8F5]">
          <Sparkles size={15} />
        </span>
        <div>
          <h2 className="text-sm font-bold uppercase tracking-[0.15em] text-[#141210]">Ops Agent</h2>
          <p className="text-[11px] text-[#8A837B]">Full operational control — reads, creates, updates and deletes across this console</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-5" data-testid="agent-messages">
        {messages.length === 1 && (
          <div className="mb-5 flex flex-wrap gap-2">
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                data-testid={`agent-suggestion-${SUGGESTIONS.indexOf(s)}`}
                onClick={() => send(s)}
                className="border border-[#DCD6CE] px-3 py-1.5 text-xs text-[#6B655F] transition-colors duration-300 hover:border-[#581820] hover:text-[#581820]"
              >
                {s}
              </button>
            ))}
          </div>
        )}
        <div className="flex flex-col gap-4">
          {messages.map((m, i) => (
            <div key={i} className={m.role === "user" ? "self-end" : "self-start"} style={{ maxWidth: "80%" }}>
              <div
                className={`px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "user"
                    ? "bg-[#141210] text-[#FAF8F5]"
                    : "border border-[#E6E2DC] bg-[#FAF8F5] text-[#141210]"
                }`}
                data-testid={m.role === "user" ? `agent-msg-user-${i}` : `agent-msg-assistant-${i}`}
              >
                {m.content || (busy && i === messages.length - 1 ? "…" : "")}
              </div>
              {m.actions && m.actions.length > 0 && (
                <div className="mt-2 flex flex-col gap-1" data-testid={`agent-actions-${i}`}>
                  {m.actions.map((a, j) => (
                    <span key={j} className="flex items-center gap-1.5 text-[11px] font-semibold text-[#1E7A5A]">
                      <CheckCircle2 size={12} /> {a}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
          <div ref={bottomRef} />
        </div>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          send();
        }}
        className="flex items-center gap-3 border-t border-[#E6E2DC] px-5 py-4"
      >
        <input
          data-testid="agent-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask the agent to do something…"
          className="flex-1 border-b border-[#DCD6CE] bg-transparent py-2 text-sm text-[#141210] placeholder:text-[#8A837B] focus:border-[#581820] focus:outline-none"
        />
        <button
          type="submit"
          data-testid="agent-send-button"
          disabled={busy || !input.trim()}
          className="flex items-center gap-2 bg-[#141210] px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.15em] text-[#FAF8F5] transition-colors duration-300 hover:bg-[#581820] disabled:opacity-60"
        >
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
          Send
        </button>
      </form>
    </div>
  );
}
